import { ArrowUpTrayIcon, PlusIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { addYears, format, subYears } from "date-fns";
import { merge, setWith } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";
import { handleRawDate } from "../../common/DateComponents";
import NoItemsRow from "../../common/NoItemsRow";
import { dateToString, getTaxYear, stringToDate, validateFloat } from "../../common/validation/Validators";
import NICBandRow from "./NICBandRow";
import StatutoryPaymentRow from "./StatutoryPaymentRow";
import StudentLoanRow from "./StudentLoanRow";

const PreviousEarnings = ({
	previousEarnings,
	confirmModal,
	engagementDate,
	lastPTFTChange,
	disabled,
	forNewEmployee,
	updatePreviousEarnings,
	nextPage,
	prevPage,
	nicBands,
	jurisdiction,
	currency,
	forReviewAgreement,
	forReview,
	nicLetters,
	confirmChange,
	studentLoans,
	statutoryPayments,
	notification,
	getTaxOptions,
	openPEImportModal,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};
	const updateArrayData = ({ array, index, newItem }) => {
		const currentValue = data[array][index] || {};
		Object.entries({ ...newItem }).map((item) => {
			const [key, value] = item;
			currentValue[key] = value ?? currentValue[key];
		});

		data[array].splice(index, 1, currentValue);
		setData(cleanDeep({ ...data }));
	};

	const previousEarningsID = useMemo(() => {
		const newID = String(previousEarnings?.id);
		if (newID === "undefined") return null;
		return newID;
	}, [previousEarnings?.id]);

	const {
		taxPeriod,
		periods,
		year: taxYear,
	} = useMemo(() => {
		const { taxPeriod, periods, year } = getTaxYear(lastPTFTChange ?? engagementDate, jurisdiction);
		console.debug({ taxPeriod, lastPTFTChange });
		return { taxPeriod, periods: periods?.map((item) => ({ label: item.taxPeriod, value: item.version })), year };
	}, [engagementDate, lastPTFTChange, jurisdiction]);

	const { taxYearStart: currentTaxYear } = useMemo(() => {
		const taxYear = getTaxYear(dateToString(new Date()), jurisdiction);
		return taxYear;
	}, [jurisdiction]);

	const isNewHire = lastPTFTChange ?? engagementDate > currentTaxYear;

	useEffect(() => {
		getTaxOptions();
	}, []);

	useEffect(() => {
		const hasPreviousEarnings = previousEarnings && Object.keys(cleanDeep(previousEarnings))?.length > 0;
		if (!hasPreviousEarnings) return;
		const fields = [
			"gross",
			"incomeTax",
			"regulatoryLimit",
			"carAllowance",
			"overtimeHours",
			"fringeBenefits",
			"pensionableIncome",
			"pensionEE",
			"pensionER",
			"nicBands",
			"studentLoans",
			"statutoryPayments",
			"overtimeTax",
			"totalPreviousOvertimeEarned",
		];
		const newData = Object.entries(cleanDeep(previousEarnings))?.reduce(
			(prev, curr) => {
				const [field, value] = curr;
				if (fields.includes(field)) prev[field] = value;
				return prev;
			},
			{ year: forReview ? previousEarnings?.year : undefined },
		);
		setData({ ...newData });
	}, [previousEarningsID]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (forNewEmployee) {
			updatePreviousEarnings(
				cleanDeep(data, { emptyStrings: false, emptyArrays: false, emptyObjects: false }),
				nextPage,
			);
			return;
		}

		confirmModal({
			input: cleanDeep(
				{ ...data, year: (forReview ? data.year : lastPTFTChange ?? engagementDate)?.substr(0, 4) },
				{ emptyStrings: false, emptyArrays: false, emptyObjects: false },
			),
			status: true,
			formType: "Previous Earnings",
		});

		if (!forReview) return;
		const newDate = forReview?.originalDate && data.start != forReview?.originalDate;
		confirmChange(
			newDate || !forReview.originalDate ? data.start : forReview.originalDate,
			!newDate,
			forReviewAgreement,
			notification,
		);
	};

	const labelNames = {
		UK: {
			gross: "Taxable Income",
			incomeTax: "Income Tax Paid",
		},
		MT: {
			gross: "Gross Primary Income (excl. Car Allowance, benefits and Qualifying OT)",
			incomeTax: "Income Tax (FSS) excl. Qualifying OT",
		},
	};

	return (
		<form
			onSubmit={onSubmit}
			className="px-8"
		>
			<div className="col-xs-12 form-section">
				<div className="col-xs-3 section-header">
					<h1 className="block-title">Previous Earnings</h1>
				</div>
				<div className="col-xs-3">
					<div
						className="form-material"
						style={{ paddingLeft: 0 }}
					>
						<label htmlFor="material-text">{"Dated For"}</label>
						<input
							value={taxPeriod}
							className="form-control"
							disabled={true}
						/>
					</div>
				</div>
				{["UK"].includes(jurisdiction) && !isNewHire ? (
					<>
						<div className="col-xs-6">
							<button
								className="btn btn-default pull-right"
								disabled={disabled}
								type="button"
								onClick={() => {
									openPEImportModal();
								}}
							>
								<ArrowUpTrayIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Import Previous Earnings</div>
							</button>
						</div>
					</>
				) : null}
			</div>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Taxable Income</h1>
				</div>
				<div className="col-xs-4">
					<div className="form-material form-container">
						<div style={{ position: "absolute", top: 5 }}>{currency}</div>
						<input
							style={{ paddingLeft: "1rem" }}
							className="form-control"
							disabled={disabled}
							id="gross"
							name="gross"
							autoComplete="off"
							value={data.gross}
							onChange={(e) => {
								updateField(e.target.name, validateFloat(e.target.value));
							}}
						/>
						<label htmlFor="gross">{labelNames[jurisdiction].gross}</label>
					</div>
				</div>
				{jurisdiction === "UK" ? (
					<div className="col-xs-4">
						<div className="form-material form-container">
							<div style={{ position: "absolute", top: 5 }}>{currency}</div>
							<input
								style={{ paddingLeft: "1rem" }}
								className="form-control"
								disabled={disabled}
								id="incomeTax"
								name="incomeTax"
								autoComplete="off"
								value={data.incomeTax}
								onChange={(e) => {
									updateField(e.target.name, validateFloat(e.target.value));
								}}
							/>
							<label htmlFor="incomeTax">{labelNames[jurisdiction].incomeTax}</label>
						</div>
					</div>
				) : null}
				{jurisdiction === "MT" ? (
					<>
						{taxYear < 2022 ? (
							<div className="col-xs-4">
								<div className="form-material form-container">
									<input
										className="form-control"
										disabled={disabled}
										id="overtimeHours"
										name="overtimeHours"
										autoComplete="off"
										value={data.overtimeHours}
										onChange={(e) => {
											updateField(e.target.name, validateFloat(e.target.value));
										}}
									/>
									<label htmlFor="overtimeHours">Qualifying Overtime Hours (15%)</label>
								</div>
							</div>
						) : null}
						{taxYear >= 2022 ? (
							<div className="col-xs-4">
								<div className="form-material form-container">
									<div style={{ position: "absolute", top: 5 }}>{currency}</div>
									<input
										style={{ paddingLeft: "1rem" }}
										className="form-control"
										disabled={disabled}
										id="totalPreviousOvertimeEarned"
										name="totalPreviousOvertimeEarned"
										autoComplete="off"
										value={data.totalPreviousOvertimeEarned}
										onChange={(e) => {
											updateField(e.target.name, validateFloat(e.target.value));
										}}
									/>
									<label htmlFor="totalPreviousOvertimeEarned">
										Overtime (Eligible for 15% Tax Deduction)
									</label>
								</div>
							</div>
						) : null}
						<div className="col-xs-4">
							<div className="form-material form-container">
								<div style={{ position: "absolute", top: 5 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control"
									disabled={disabled}
									id="carAllowance"
									name="carAllowance"
									autoComplete="off"
									value={data.carAllowance}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
								/>
								<label htmlFor="carAllowance">Car Allowance</label>
							</div>
						</div>
						<div className="col-xs-4">
							<div className="form-material form-container">
								<div style={{ position: "absolute", top: 5 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control"
									disabled={disabled}
									id="fringeBenefits"
									name="fringeBenefits"
									autoComplete="off"
									value={data.fringeBenefits}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
								/>
								<label htmlFor="fringeBenefits">Fringe Benefits (excl. Car Cash Allowance)</label>
							</div>
						</div>
					</>
				) : null}
				{jurisdiction === "UK" && !isNewHire ? (
					<>
						<div className="col-xs-4">
							<div className="form-material form-container">
								<div style={{ position: "absolute", top: 5 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control"
									disabled={disabled}
									id="regulatoryLimit"
									name="regulatoryLimit"
									autoComplete="off"
									value={data.regulatoryLimit}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
								/>
								<label htmlFor="regulatoryLimit">Tax Not Deducted Due to Regulatory Limit</label>
							</div>
						</div>
					</>
				) : null}
				<div className="clearfix" />
			</div>
			{jurisdiction === "UK" && !isNewHire ? (
				<>
					<div className="col-xs-12 form-section">
						<div className="col-xs-6 section-header">
							<h1 className="block-title">Nicable Income</h1>
						</div>
						<div className="col-xs-6">
							<button
								className="btn btn-success pull-right"
								disabled={
									disabled ||
									(nicBands?.length || 1) * (nicLetters?.length || 1) == data.nicBands?.length
								}
								type="button"
								onClick={() => {
									updateField("nicBands", [
										...(Array.isArray(data.nicBands) ? data.nicBands : []),
										{},
									]);
								}}
							>
								<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Add NIC Band</div>
							</button>
						</div>
						<div className="clearfix" />
						{data.nicBands?.length > 0 && nicBands?.length > 0 ? (
							<>
								{data.nicBands?.map((band, index) => {
									return (
										<NICBandRow
											index={index}
											filteredList={nicBands}
											nicBands={data.nicBands}
											nicBandList={nicBands}
											nicLettersList={nicLetters}
											nicPeriods={periods}
											disabled={disabled}
											updateNicBand={({ band, nicPeriod, letter, income, paidEE, paidER }) => {
												updateArrayData({
													array: "nicBands",
													index,
													newItem: { band, nicPeriod, letter, income, paidEE, paidER },
												});
											}}
											setNicBands={(nicBands) => setData({ ...data, nicBands })}
										/>
									);
								})}
							</>
						) : (
							<table className="js-table-sections table table-hover">
								<NoItemsRow name="NIC Bands" />
							</table>
						)}
					</div>
					<div className="col-xs-12 form-section">
						<div className="col-xs-12 section-header">
							<h1 className="block-title">Pensionable Income</h1>
						</div>
						<div className="col-xs-4">
							<div className="form-material form-container">
								<div style={{ position: "absolute", top: 5 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control"
									disabled={disabled}
									id="pensionableIncome"
									name="pensionableIncome"
									autoComplete="off"
									value={data.pensionableIncome}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
								/>
								<label htmlFor="pensionableIncome">Pensionable Income</label>
							</div>
						</div>
						<div className="col-xs-4">
							<div className="form-material form-container">
								<div style={{ position: "absolute", top: 5 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control"
									disabled={disabled}
									id="pensionEE"
									name="pensionEE"
									autoComplete="off"
									value={data.pensionEE}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
								/>
								<label htmlFor="pensionEE">Pension Contributions Employee</label>
							</div>
						</div>
						<div className="col-xs-4">
							<div className="form-material form-container">
								<div style={{ position: "absolute", top: 5 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control"
									disabled={disabled}
									id="pensionER"
									name="pensionER"
									autoComplete="off"
									value={data.pensionER}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
								/>
								<label htmlFor="pensionER">Pension Contributions Employer</label>
							</div>
						</div>
					</div>
					<div className="col-xs-12 form-section">
						<div className="col-xs-6 section-header">
							<h1 className="block-title">Student Loans</h1>
						</div>
						<div className="col-xs-6">
							<button
								className="btn btn-success pull-right"
								disabled={disabled || studentLoans?.length == data.studentLoans?.length}
								type="button"
								onClick={() => {
									updateField("studentLoans", [
										...(Array.isArray(data.studentLoans) ? data.studentLoans : []),
										{},
									]);
								}}
							>
								<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Add Student Loan</div>
							</button>
						</div>
						<div className="clearfix" />
						{data.studentLoans?.length > 0 && studentLoans?.length > 0 ? (
							<>
								{data.studentLoans?.map((studentLoan, index) => {
									return (
										<StudentLoanRow
											index={index}
											filteredList={studentLoans?.filter(
												(item) =>
													!data.studentLoans
														?.map((studentLoan) => studentLoan?.type)
														.includes(String(item.value)),
											)}
											selectedStudentLoans={data.studentLoans}
											studentLoans={studentLoans}
											disabled={disabled}
											updateStudentLoan={({ type, amount }) => {
												updateArrayData({
													array: "studentLoans",
													index,
													newItem: { type, amount },
												});
											}}
											setStudentLoan={(studentLoans) => setData({ ...data, studentLoans })}
										/>
									);
								})}
							</>
						) : (
							<table className="js-table-sections table table-hover">
								<NoItemsRow name="Student Loans" />
							</table>
						)}
					</div>
					<div className="col-xs-12 form-section">
						<div className="col-xs-6 section-header">
							<h1 className="block-title">Statutory Payments</h1>
						</div>
						<div className="col-xs-6">
							<button
								className="btn btn-success pull-right"
								disabled={disabled || statutoryPayments?.length == data.statutoryPayments?.length}
								type="button"
								onClick={() => {
									updateField("statutoryPayments", [
										...(Array.isArray(data.statutoryPayments) ? data.statutoryPayments : []),
										{},
									]);
								}}
							>
								<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Add Statutory Payment</div>
							</button>
						</div>
						<div className="clearfix" />
						{data.statutoryPayments?.length > 0 && statutoryPayments?.length > 0 ? (
							<>
								{data.statutoryPayments?.map((statutoryPayment, index) => {
									return (
										<StatutoryPaymentRow
											index={index}
											filteredList={statutoryPayments?.filter(
												(item) =>
													!data.statutoryPayments
														?.map((statutoryPayment) => statutoryPayment?.type)
														.includes(String(item.value)),
											)}
											selectedStatutoryPayments={data.statutoryPayments}
											statutoryPayments={statutoryPayments}
											disabled={disabled}
											updateStatutoryPayment={({ type, amount }) => {
												updateArrayData({
													array: "statutoryPayments",
													index,
													newItem: { type, amount },
												});
											}}
											setStatutoryPayment={(statutoryPayments) =>
												setData({ ...data, statutoryPayments })
											}
										/>
									);
								})}
							</>
						) : (
							<table className="js-table-sections table table-hover">
								<NoItemsRow name="Statutory Payments" />
							</table>
						)}
					</div>
				</>
			) : null}
			{jurisdiction === "MT" ? (
				<div className="col-xs-12 form-section">
					<div className="col-xs-12 section-header">
						<h1 className="block-title">Deductions</h1>
					</div>
					<div className="col-xs-4">
						<div className="form-material form-container">
							<div style={{ position: "absolute", top: 5 }}>{currency}</div>
							<input
								style={{ paddingLeft: "1rem" }}
								className="form-control"
								disabled={disabled}
								id="incomeTax"
								name="incomeTax"
								autoComplete="off"
								value={data.incomeTax}
								onChange={(e) => {
									updateField(e.target.name, validateFloat(e.target.value));
								}}
							/>
							<label htmlFor="incomeTax">{labelNames[jurisdiction].incomeTax}</label>
						</div>
					</div>
					{taxYear >= 2022 ? (
						<div className="col-xs-4">
							<div className="form-material form-container">
								<div style={{ position: "absolute", top: 5 }}>{currency}</div>
								<input
									style={{ paddingLeft: "1rem" }}
									className="form-control"
									disabled={disabled}
									id="overtimeTax"
									name="overtimeTax"
									autoComplete="off"
									value={data.overtimeTax}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
								/>
								<label htmlFor="overtimeTax">Tax Deductions (Eligible Overtime)</label>
							</div>
						</div>
					) : null}
				</div>
			) : null}

			<div className="clearfix" />
			<div>
				{!disabled && (
					<button
						className="btn btn-success right"
						type="submit"
					>
						{!previousEarningsID
							? `Save${forNewEmployee ? " & Continue" : ""}`
							: "Update Previous Earnings"}
					</button>
				)}
				{forNewEmployee && (
					<>
						<button
							className="btn btn-default pull-right mr-4"
							onClick={nextPage}
						>
							Skip
						</button>
						{prevPage && (
							<button
								className="btn btn-default"
								onClick={prevPage}
							>
								Back
							</button>
						)}
					</>
				)}
			</div>
		</form>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { forReview, forNewEmployee } = ownProps;

	let previousEarnings = state.employee?.previousEarnings?.[state.employee?.previousEarnings?.length - 1];
	if (forNewEmployee) previousEarnings = state.employee.previousEarnings?.[0] || {};
	if (forReview) previousEarnings = forReview;
	const { niLetters: nicLetters, nicBands, studentLoans } = { ...state.taxOptions };

	// date where employee changed from PT to FT or vice versa
	const salaries = state.employee?.salaries;
	let lastPTFTChange = state.employee?.employeeDetails?.engagementDate;
	let lastJobType = null;

	// console.log('changeSalary-salaries',salaries)/
	salaries?.map((salary) => {
		if (salary?.employmentTypeName != lastJobType) {
			if (salary?.employmentTypeName == "Full-time") {
				// it only changes when it goes from PT to FT
				lastPTFTChange = salary?.start;
			}
			lastJobType = salary?.employmentTypeName;
		}
	});

	return {
		currency: state.company?.settings?.currency,

		nicBands,
		nicLetters,
		studentLoans: [
			...(Array.isArray(studentLoans) ? studentLoans : []),
			{ label: "Postgrad Loan", value: "postgradLoan" },
		],
		statutoryPayments: [
			{ label: "Statutory Maternity Pay (SMP)", value: "SMP" },
			{ label: "Statutory Paternity Pay (SPP)", value: "SPP" },
			{ label: "Statutory Adoption Pay (SAP)", value: "SAP" },
			{ label: "Statutory Shared Parental Pay (ShPP)", value: "ShPP" },
			{ label: "Statutory Parental Bereavement Pay (SPBP)", value: "SPBP" },
		],
		jurisdiction: state.company?.settings?.jurisdiction,
		previousEarnings,
		engagementDate: state.employee?.employeeDetails?.engagementDate,
		lastPTFTChange,
		disabled: state?.permissions?.my?.permissions?.people == "Read",
	};
};

const mapDispatchToProps = (dispatch) => ({
	openPEImportModal: () => dispatch(EMPLOYEEACTIONS.togglePEImportModal(true)),
	confirmModal: (options) => dispatch(EMPLOYEEACTIONS.confirmModal(options)),
	getTaxOptions: () => dispatch(EMPLOYEEACTIONS.getTaxOptions()),
	updatePreviousEarnings: (input, onSuccess) => dispatch(EMPLOYEEACTIONS.updatePreviousEarnings(input, onSuccess)),
	confirmChange: (date, replace, agreement, notification) =>
		dispatch(EMPLOYEEACTIONS.confirmChange({ date, replace, agreement, notification })),
});

export default connect(mapStateToProps, mapDispatchToProps)(PreviousEarnings);
